import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Services() {
  const navigate = useNavigate();

  const mainServices = [
    {
      icon: 'ri-home-4-line',
      title: 'Mortgage Refinance Lead Generation',
      description: 'Specialized lead generation for mortgage refinancing companies targeting homeowners looking to reduce monthly payments, access equity, and secure better rates through comprehensive digital marketing strategies.',
      features: [
        'Homeowner targeting campaigns',
        'Equity assessment tools',
        'Rate comparison landing pages',
        'Credit score optimization',
        'Refinance calculators',
        'Real-time lead delivery'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20mortgage%20refinance%20consultant%20working%20with%20homeowners%20in%20bright%20modern%20office%2C%20reviewing%20loan%20documents%20and%20interest%20rates%20on%20computer%20screen%2C%20calculator%20and%20house%20model%20on%20desk&width=600&height=400&seq=mortgage-service&orientation=landscape'
    },
    {
      icon: 'ri-robot-2-line',
      title: 'Pre-underwriting AI Solutions',
      description: 'Advanced AI-powered pre-qualification and underwriting assistance that streamlines loan approval processes with intelligent document analysis, risk assessment, and automated qualification scoring.',
      features: [
        'AI document analysis',
        'Automated risk scoring',
        'Income verification AI',
        'Credit assessment tools',
        'Compliance checking',
        'Real-time qualification'
      ],
      image: 'https://readdy.ai/api/search-image?query=AI%20technology%20specialist%20demonstrating%20automated%20underwriting%20software%20on%20multiple%20computer%20screens%20showing%20document%20analysis%20and%20risk%20assessment%20dashboards%20in%20modern%20fintech%20office%20environment&width=600&height=400&seq=ai-underwriting&orientation=landscape'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Debt Settlement Lead Generation',
      description: 'Generate qualified leads for debt consolidation and settlement services, targeting individuals seeking financial relief, debt reduction strategies, and improved payment terms.',
      features: [
        'Debt assessment campaigns',
        'Financial hardship targeting',
        'Settlement calculators',
        'Credit repair integration',
        'Payment plan optimization',
        'Qualified lead scoring'
      ],
      image: 'https://readdy.ai/api/search-image?query=Compassionate%20debt%20settlement%20advisor%20helping%20stressed%20family%20with%20financial%20documents%20in%20comfortable%20consultation%20room%2C%20showing%20debt%20reduction%20charts%20and%20payment%20plans%20on%20tablet&width=600&height=400&seq=debt-settlement&orientation=landscape'
    },
    {
      icon: 'ri-phone-line',
      title: 'Inbound Call Management',
      description: 'Optimize inbound call lead capture and qualification processes with advanced call tracking, routing systems, and conversion optimization to maximize ROI from all marketing channels.',
      features: [
        'Call tracking systems',
        'Intelligent call routing',
        'Lead qualification scripts',
        'Call recording & analysis',
        'Performance analytics',
        'Conversion optimization'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20call%20center%20team%20managing%20inbound%20leads%20with%20headsets%20and%20multiple%20computer%20monitors%20showing%20call%20analytics%20and%20customer%20management%20systems%20in%20modern%20office%20environment&width=600&height=400&seq=inbound-calls&orientation=landscape'
    },
    {
      icon: 'ri-health-book-line',
      title: 'Health Insurance Lead Generation',
      description: 'Connect health insurance providers with qualified prospects seeking comprehensive coverage, Medicare plans, and competitive rates through targeted digital marketing campaigns.',
      features: [
        'Medicare enrollment campaigns',
        'Health plan comparisons',
        'Subsidy qualification tools',
        'Open enrollment targeting',
        'Provider network searches',
        'Coverage calculators'
      ],
      image: 'https://readdy.ai/api/search-image?query=Healthcare%20insurance%20specialist%20helping%20family%20review%20health%20coverage%20options%20on%20computer%20screen%20in%20bright%20medical%20office%2C%20insurance%20documents%20and%20comparison%20charts%20visible&width=600&height=400&seq=health-insurance&orientation=landscape'
    },
    {
      icon: 'ri-shield-keyhole-line',
      title: 'Home Security Lead Generation',
      description: 'Generate leads for home security companies targeting homeowners interested in protecting their property and family with advanced security systems, smart home integration, and monitoring services.',
      features: [
        'Homeowner security targeting',
        'Smart home integration',
        'Security system comparisons',
        'Installation scheduling',
        'Monitoring service options',
        'Emergency response features'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20security%20specialist%20demonstrating%20smart%20home%20security%20system%20to%20interested%20homeowner%20family%20in%20modern%20living%20room%2C%20showcasing%20control%20panel%20and%20mobile%20app%20interface&width=600&height=400&seq=home-security&orientation=landscape'
    }
  ];

  const additionalServices = [
    {
      icon: 'ri-line-chart-line',
      title: 'Analytics & Reporting',
      description: 'Comprehensive analytics and detailed reporting to track performance and optimize campaigns.'
    },
    {
      icon: 'ri-phone-line',
      title: 'Call Tracking',
      description: 'Advanced call tracking solutions to measure phone lead quality and campaign effectiveness.'
    },
    {
      icon: 'ri-pages-line',
      title: 'Landing Page Optimization',
      description: 'High-converting landing pages designed specifically for mortgage, debt, and security industries.'
    },
    {
      icon: 'ri-mail-line',
      title: 'Email Marketing',
      description: 'Automated email sequences for lead nurturing and customer retention campaigns.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery & Strategy',
      description: 'We analyze your business, target audience, and competition to develop a customized lead generation strategy.'
    },
    {
      step: '02',
      title: 'Campaign Setup',
      description: 'Our experts set up optimized campaigns across multiple channels with proper tracking and targeting.'
    },
    {
      step: '03',
      title: 'Launch & Monitor',
      description: 'We launch your campaigns and continuously monitor performance to ensure optimal results.'
    },
    {
      step: '04',
      title: 'Optimize & Scale',
      description: 'Based on data insights, we optimize campaigns and scale successful strategies for maximum ROI.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-object py-16 md:py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(30, 64, 175, 0.8), rgba(30, 64, 175, 0.8)), url('https://readdy.ai/api/search-image?query=Professional%20digital%20marketing%20team%20collaborating%20in%20modern%20office%20with%20multiple%20screens%20showing%20analytics%20dashboards%2C%20SEO%20tools%2C%20and%20advertising%20campaigns%2C%20bright%20workspace%20with%20technology%20focus&width=1920&height=800&seq=services-hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-2xl md:text-5xl lg:text-6xl font-bold text-white mb-4 md:mb-6">
              Comprehensive Lead Generation Services
            </h1>
            <p className="text-base md:text-xl text-blue-100 mb-6 md:mb-8">
              From SEO and SEM to AI-powered solutions, we provide end-to-end digital marketing services designed to generate qualified leads for international clients.
            </p>
            <Button 
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-black hover:bg-gray-100"
            >
              <i className="ri-calendar-line mr-2 text-black"></i>
              Schedule Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
              Our Core Services
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized digital marketing services tailored for high-converting industries with proven track records of success.
            </p>
          </div>

          <div className="space-y-12 md:space-y-20">
            {mainServices.map((service, index) => (
              <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8 md:gap-12`}>
                <div className="w-full lg:w-1/2">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full rounded-2xl shadow-lg object-cover object-top"
                  />
                </div>
                <div className="w-full lg:w-1/2">
                  <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-blue-100 rounded-2xl mb-4 md:mb-6">
                    <i className={`${service.icon} text-xl md:text-2xl text-blue-600`}></i>
                  </div>
                  <h3 className="text-xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 md:mb-6">{service.title}</h3>
                  <p className="text-sm md:text-lg text-gray-600 mb-5 md:mb-8 leading-relaxed">{service.description}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center">
                        <div className="w-2 h-2 bg-blue-600 rounded-full mr-3 flex-shrink-0"></div>
                        <span className="text-gray-700 text-sm md:text-base">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
              Additional Services
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              Complementary services to enhance your lead generation campaigns and maximize conversion rates.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {additionalServices.map((service, index) => (
              <div key={index} className="bg-white p-5 md:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow text-center">
                <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-blue-100 rounded-full mx-auto mb-4 md:mb-6">
                  <i className={`${service.icon} text-xl md:text-2xl text-blue-600`}></i>
                </div>
                <h3 className="text-sm md:text-xl font-bold text-gray-900 mb-2 md:mb-4">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed text-xs md:text-sm hidden md:block">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
              Our Proven Process
            </h2>
            <p className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach to lead generation that delivers consistent results and measurable ROI.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
            {process.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 md:w-20 md:h-20 flex items-center justify-center bg-blue-600 text-white text-lg md:text-2xl font-bold rounded-full mx-auto mb-4 md:mb-6">
                  {step.step}
                </div>
                <h3 className="text-sm md:text-xl font-bold text-gray-900 mb-2 md:mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed text-xs md:text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">
            Ready to Generate More Qualified Leads?
          </h2>
          <p className="text-base md:text-xl text-blue-100 mb-6 md:mb-8">
            Let&apos;s discuss how our proven lead generation services can help grow your business and increase your ROI.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-black hover:bg-gray-100"
            >
              <i className="ri-phone-line mr-2 text-black"></i>
              Get Free Consultation
            </Button>
            <Button 
              variant="outline"
              size="lg"
              onClick={() => navigate('/services')}
              className="border-white text-white hover:bg-white hover:text-blue-600"
            >
              <i className="ri-eye-line mr-2"></i>
              View Our Results
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
